import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDeviceStates } from '@/constants/DeviceStates.jsx';
import { useTranslate } from 'react-admin';
import { Palette } from '@/themes/themeOverrides/Palette';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import DeviceCodeErrorTooltip from '@/components/device/DeviceCodeErrorTooltip.jsx';
import PropTypes from 'prop-types';

const UPTIME_BREAKPOINTS = [25, 50, 75];
const TRANSACTIONS_BREAKPOINTS = [250, 500, 750];

const getStateIconColors = (value, breakpoints) => {
    if (value <= breakpoints[0]) {
        return {
            backgroundColor: Palette.red.red200,
            boxShadow: `inset 0 0 0 1.5px ${Palette.red.red400}`,
        };
    }
    if (value <= breakpoints[1]) {
        return {
            backgroundColor: Palette.yellow.yellow400,
            boxShadow: `inset 0 0 0 1.5px ${Palette.yellow.yellow700}`,
        };
    }
    return {
        backgroundColor: Palette.green.green300,
        boxShadow: `inset 0 0 0 1.5px ${Palette.green.green600}`,
    };
};

const StyledDeviceServiceRequestStateField = styled(Box)(() => ({
    width: 'max-content',
    '& .state-details': {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginLeft: 28,
    },
    '& .state-details-group': {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    '& .state-details-icon': {
        width: 10,
        height: 10,
        borderRadius: '3px',
    },
    '& .state-details-number': {
        fontSize: 11,
    },
}));

const DeviceServiceRequestStateField = ({ record, className }) => {
    const translate = useTranslate();
    const deviceStates = useDeviceStates();
    const uptimeNumber = formatNumber((record?.uptime ?? 0) * 100, 0);
    const transactionsNumber = formatNumber(record?.transactionsCount ?? 0, 0);

    const uptimeIconColors = getStateIconColors(uptimeNumber, UPTIME_BREAKPOINTS);
    const transactionsIconColors = getStateIconColors(transactionsNumber, TRANSACTIONS_BREAKPOINTS);

    if (!record || !record.device || !record.device.state) return null;

    const state = deviceStates.find(s => s.id === record.device.state);

    if (!state) return <span>{translate('resources.transaction.fields.unknown')}</span>;

    const stateContent = (
        <Box className={className} style={{ display: 'flex', alignItems: 'center' }}>
            {state.icon && <span style={{ marginRight: '8px' }}>{state.icon}</span>}
            <span style={{ whiteSpace: 'nowrap', marginTop: '-3px' }}>{state.name}</span>
        </Box>
    );

    return (
        <StyledDeviceServiceRequestStateField>
            {state.tooltip ? <DeviceCodeErrorTooltip currentErrorCodes={record.device.currentErrorCodes}>{stateContent}</DeviceCodeErrorTooltip> : stateContent}
            <Box className='state-details'>
                <Box className='state-details-group'>
                    <div className='state-details-icon' style={uptimeIconColors}></div>
                    <p className='state-details-number'>
                        {uptimeNumber}% {translate('common.uptime')}
                    </p>
                </Box>
                <Box className='state-details-group'>
                    <div className='state-details-icon' style={transactionsIconColors}></div>
                    <p className='state-details-number'>
                        {transactionsNumber} {translate('common.transactionsCount')}
                    </p>
                </Box>
            </Box>
        </StyledDeviceServiceRequestStateField>
    );
};

DeviceServiceRequestStateField.propTypes = {
    record: PropTypes.shape({
        uptime: PropTypes.number,
        transactionsCount: PropTypes.number,
        device: PropTypes.shape({
            state: PropTypes.string,
            currentErrorCodes: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
        }),
    }),
    className: PropTypes.string,
};
export default DeviceServiceRequestStateField;
